import React, { useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useFlowAccount } from './hooks';
import { AccountFieldsCard, AccountOppsPanel } from './AccountDetailPanels';
import { AccountTimeline } from './AccountTimeline';
import { AccountSignalsPanel } from './AccountSignalsPanel';
import { AccountIntelPanel } from './AccountIntelPanel';
import { AccountWorkspacesSection } from './AccountWorkspacesSection';
import { sfdcRecordUrl } from '../links';
import { ContactFlowTable } from '../contact/ContactFlowTable';
import { useEnrollQueue, useFlowChatSessions, useFlowChatSessionList } from '../EnrollQueue';
import { QuadTab } from '../QuadTab';
import { v1RightCardClass } from './hierarchy/moduleHeight';
import { AccountHierarchyTab } from './hierarchy/AccountHierarchyTab';
import { AccountHierarchyLinks } from './hierarchy/AccountHierarchyLinks';
import { CONTACTS_MODULE_TESTID, useFocusParam } from './useFocusParam';
import { useAccountTab } from './useAccountTab';
import type { AccountDrilldownTab } from '@shared/flow/account-drilldown-tabs';

/**
 * Account drill-down (Flow spec, Notion 3927): header strip + a plain Fields
 * quad (left) + a tabbed Timeline/Opps/Intelligence quad (right) + the
 * full-width contact component filtered to the account (mount #2).
 * `accountId` = the SFDC 18-char id from the route.
 *
 * The left quad used to be a Fields/Notes tab strip; the Notes tab was
 * removed (Bucket B U4, R3 — Notes UI paused pending Terra's
 * mirror-vs-live-callback consistency decision) leaving Fields as a plain
 * header, matching the prototype's post-removal quad-head.
 */

/** The tabs V1's right card declares; the URL's `?accountTab=` picks among them (see `useAccountTab`). */
const V1_TABS = ['timeline', 'opps', 'signals', 'intel', 'hierarchy'] as const satisfies readonly AccountDrilldownTab[];

function HeaderField({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <span>
      <span className="block text-[10px] uppercase tracking-wide text-content-tertiary dark:text-nightshade-500">{k}</span>
      <span className="text-[13px] font-semibold text-content-primary dark:text-nightshade-100">
        {v == null || v === '' ? '—' : v}
      </span>
    </span>
  );
}

// Fixed height, NOT grid-stretch-dependent (Spencer feedback, 2026-08-14,
// third pass — see history at the top of this file / task notes). Two
// prior attempts both leaned on CSS grid's `align-items: stretch` to give
// the two columns a shared height, and both broke because "shared height"
// and "each column's height is a function of the OTHER column's content"
// are the same thing under stretch:
//   - Attempt 1: per-body `max-h-[Npx]` (340/420/520, differed per tab) +
//     stretch → the capped body couldn't grow to fill the taller stretched
//     card, leaving a dead gap under the shorter column's content.
//   - Attempt 2: `items-start` (opt out of stretch) → no shared height at
//     all; Spencer wants the columns visually matched.
//   - Attempt 3: no cap, `flex-1 min-h-0 overflow-y-auto`, plain stretch →
//     when Timeline's natural content is tall, the grid ROW sizes to that,
//     and stretch then forces the Fields card to match — but Fields'
//     `columns-2` CSS-column content re-balances to fill whatever height
//     it's given, so it doesn't just look padded, it balances two columns
//     of field rows across a much taller box than they need, leaving real
//     dead space below the last field row. Confirmed via
//     `getBoundingClientRect()`: both cards stretched to 824px against a
//     ~591px natural Fields height.
// Fix: give the OUTER wrapper an explicit fixed height so neither column's
// content can ever influence the other's box. 592px = the Fields card's
// own natural height (header + content, no stretch), measured live via
// `browser-ctl eval` against CCTESTACC0004 (Adventure Works Test, the
// account with a long Account Timeline that broke both prior attempts):
// temporarily set `alignSelf: 'start'` on the Fields card in a real render
// and read `getBoundingClientRect().height` → 591.5px, rounded up.
//
// Module-level (not per-render locals) since U4c: the `'resolving'` scaffold
// below reuses the same card geometry, so the loading page is the same shape
// as the page it becomes.
const PANEL_CLASS = 'bg-white dark:bg-nightshade-900 border border-secondary dark:border-nightshade-700 rounded-lg flex flex-col h-[592px]';
const PANEL_BODY = 'p-3 flex-1 min-h-0 overflow-y-auto';

/**
 * The `'resolving'` state (plan U4 / D10-D11): this id had no mirror row, so
 * the backend fired an on-demand Middleman resolve and answered
 * `200 { state: 'resolving' }`; `useFlowAccount` is polling until the row
 * lands (or the bounded give-up window elapses, after which the hook reports
 * `null` and the caller's "Account not found." branch takes over).
 *
 * Deliberately the NORMAL page scaffold — same breadcrumb, header strip and
 * quad geometry — with inline loading inside it, rather than a bare centered
 * spinner: the rep is watching a page fill in, not waiting on a blank screen,
 * and nothing jumps when it settles.
 *
 * The Contacts section renders as an inline note instead of the real
 * `ContactFlowTable`: the force-fetch mirrors the ACCOUNT row only —
 * Middleman's `/salesforce/resolve` resolves ids to parent-account fields and
 * has no contact-record fetch at all (plan Q6, verified) — so contacts cannot
 * arrive until the next hourly bulk pass, and mounting the roster would just
 * render a permanently empty table with no explanation.
 */
function AccountResolvingScaffold({ accountId, onBack }: { accountId: string; onBack?: () => void }) {
  const inlineLoading = (
    <div className="flex items-center gap-1.5 text-[12px] text-content-tertiary dark:text-nightshade-500">
      <Loader2 className="w-3 h-3 animate-spin" aria-hidden="true" />
      Loading…
    </div>
  );

  return (
    <div className="space-y-3" data-testid="account-resolving">
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-[12px] text-content-secondary dark:text-nightshade-400 hover:text-content-primary dark:hover:text-nightshade-100"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Accounts
        </button>
      )}

      {/* Header strip — same card as the settled page, with the status line
          where the account name will appear. */}
      <div className="bg-white dark:bg-nightshade-900 border border-secondary dark:border-nightshade-700 rounded-lg px-4 py-3 flex items-center gap-6 flex-wrap">
        <span className="flex items-center gap-2 text-[16px] font-bold text-content-primary dark:text-nightshade-100">
          <Loader2 className="w-4 h-4 animate-spin text-content-tertiary dark:text-nightshade-500" aria-hidden="true" />
          Fetching from Salesforce…
        </span>
        <span className="text-[12px] text-content-secondary dark:text-nightshade-400">
          This account isn't in Terra's mirror yet — full details and contacts sync within the hour.
        </span>
        <span className="ml-auto flex items-center gap-3 text-[12px]">
          <a
            href={sfdcRecordUrl('Account', accountId)}
            target="_blank" rel="noreferrer"
            className="text-content-action dark:text-blueberry-400 hover:underline"
          >
            SFDC ↗
          </a>
        </span>
      </div>

      <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-3">
        <div className={PANEL_CLASS}>
          <div className="px-3 pt-2 pb-1 border-b border-secondary dark:border-nightshade-700">
            <span className="text-[12px] font-semibold text-content-primary dark:text-nightshade-100">Fields</span>
          </div>
          <div className={PANEL_BODY}>{inlineLoading}</div>
        </div>
        <div className={PANEL_CLASS}>
          <div className="px-3 pt-2 pb-1 border-b border-secondary dark:border-nightshade-700">
            <span className="text-[12px] font-semibold text-content-primary dark:text-nightshade-100">Account Timeline</span>
          </div>
          <div className={PANEL_BODY}>{inlineLoading}</div>
        </div>
      </div>

      <div data-testid={CONTACTS_MODULE_TESTID}>
        <div className="text-[13px] font-semibold text-content-primary dark:text-nightshade-100 mb-1.5">Contacts</div>
        <div className="bg-white dark:bg-nightshade-900 border border-secondary dark:border-nightshade-700 rounded-lg px-3 py-4 text-[12px] text-content-tertiary dark:text-nightshade-500">
          The on-demand fetch brings in the account record only — this roster fills in on the next hourly Salesforce sync.
        </div>
      </div>
    </div>
  );
}

export interface AccountDrillDownProps {
  accountId: string;
  /**
   * Builds a company's drilldown route. Optional: defaults to the command-center route shape (the same
   * URL the beta shell uses), so the mini-app mount does not need to pass it.
   */
  accountHref?: (salesforceAccountId: string) => string;
  onBack?: () => void;
}

const defaultAccountHref = (salesforceAccountId: string): string => `/flow/command-center/accounts/${salesforceAccountId}`;

export function AccountDrillDown({ accountId, accountHref = defaultAccountHref, onBack }: AccountDrillDownProps) {
  const { data, isLoading, error } = useFlowAccount(accountId);
  const { tab: rightTab, setTab: setRightTab } = useAccountTab(V1_TABS, 'timeline');
  // Hierarchy tab's "View contacts" (`?focus=contacts`) scrolls the roster into view; "View deals" is
  // `?accountTab=opps`, read by `useAccountTab` above.
  useFocusParam(Boolean(data && data.state !== 'resolving' && data.account));
  // "See Clay Users" (QA ask, 2026-08-26): the Workspaces section's link
  // switches the Contacts roster below it to the Clay Users preset — an
  // imperative cross-component request, not a URL deep-link (see
  // ContactFlowTableProps.requestedViewId's doc comment for why `?view=`
  // alone doesn't work here).
  const [requestedContactViewId, setRequestedContactViewId] = useState<string>();

  // "Chat" header link (R7 follow-up, Spencer QA): an explicit, one-click way
  // to start (or resume) a chat scoped to THIS account, rather than relying
  // on the global FAB's last-viewed-session default. Deliberately still an
  // EXPLICIT user action (D-C) — it does not adopt/auto-start anything on
  // mere page view, only on this link's click.
  const { openChat } = useEnrollQueue();
  const { createSession, setActiveSessionId } = useFlowChatSessions();
  const chatSessions = useFlowChatSessionList();
  const handleChatClick = () => {
    const existing = chatSessions.find((s) => s.accountId === accountId);
    const sessionId = existing ? existing.sessionId : createSession(accountId, data?.account?.name ?? '');
    setActiveSessionId(sessionId);
    openChat();
  };

  if (isLoading) {
    return <div className="p-8 text-center text-[12px] text-content-tertiary dark:text-nightshade-500">Loading account…</div>;
  }
  // The account-less `'resolving'` member (plan U4b/U4c): an unmirrored 001 id
  // whose on-demand Salesforce resolve the backend just fired, with
  // `useFlowAccount` polling until the row lands. Checked BEFORE `error`
  // deliberately: `retry: false` means one flaky poll flips `error` on while
  // the last good envelope (still `'resolving'`) is retained and polling
  // continues, and swapping the filling-in page for "Failed to load account."
  // mid-resolve — then back again on the next successful poll — would be pure
  // flicker. A give-up (or a real 404) surfaces as `data === null` below.
  if (data?.state === 'resolving') {
    return <AccountResolvingScaffold accountId={accountId} onBack={onBack} />;
  }
  if (error) {
    return <div className="p-8 text-center text-[12px] text-content-danger dark:text-red-400">Failed to load account.</div>;
  }
  // Also the give-up path (plan D11): once the bounded resolve window
  // elapses, `useFlowAccount` returns null — the same value a 404 gives —
  // so the resolve that never landed ends in the truly-not-found state.
  if (!data) {
    return <div className="p-8 text-center text-[12px] text-content-tertiary dark:text-nightshade-500">Account not found.</div>;
  }

  const account = data.account;

  // Viewable split (plan D3/D4, U2): any mirrored, non-deleted account
  // renders the full drill-down below — 'not_synced' (old-backend skew, kept
  // one deploy cycle for tolerance) is treated identically to 'synced'. The
  // old minimal "Account not synced to Terra" dead-end panel is gone; that
  // was a visibility rule, not a sync fact (see the schema's module comment
  // in src/shared/flow/account-view.ts). `in_list_universe === false` means
  // the account is fully viewable but out of the ranked lists (e.g. no
  // ClayDR/GS/real owner) — surfaced below as a banner, not a dead end.
  // `in_list_universe` absent (old backend, reverse skew) is treated as
  // in-universe: no banner.
  const notInListUniverse = data.in_list_universe === false;

  // Card geometry (the fixed `h-[592px]` and why it is fixed) lives in
  // `PANEL_CLASS`/`PANEL_BODY` at module scope — the `'resolving'` scaffold
  // shares it.
  //
  // `AccountFieldsCard` has no height of its own (plain CSS-columns content) —
  // `PANEL_CLASS`'s fixed height is what bounds the Fields column now.
  // Opps had a taller dedicated cap (Bucket B U8, Decision 10 — the rich
  // expand can run Key Personas + a 10-item interleaved activity stream);
  // that distinction no longer matters — every body now scrolls internally
  // within the same fixed card height instead of capping to its own ceiling
  // (which is why every body below is the same `PANEL_BODY`).

  return (
    <div className="space-y-3">
      {/* Breadcrumb */}
      {onBack && (
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-[12px] text-content-secondary dark:text-nightshade-400 hover:text-content-primary dark:hover:text-nightshade-100"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Accounts
        </button>
      )}

      {/* Viewable-but-not-listed banner (plan D3/U2) — informational, not a
          gate: the account renders fully below regardless. No `suppressed`
          banner (D6 cut) — `suppressed` stays a plain field inside
          AccountFieldsCard, unchanged by this plan. */}
      {notInListUniverse && (
        <div
          data-testid="account-not-in-list-universe-banner"
          className="flex items-center gap-1.5 rounded-md border border-content-warning/40 bg-bg-yellow-light px-3 py-1.5 text-[12px] text-content-primary dark:border-amber-700/50 dark:text-nightshade-100"
        >
          Not in anyone's book — not shown in ranked lists.
        </div>
      )}

      {/* Header strip */}
      <div className="bg-white dark:bg-nightshade-900 border border-secondary dark:border-nightshade-700 rounded-lg px-4 py-3 flex items-center gap-6 flex-wrap">
        <span className="text-[16px] font-bold text-content-primary dark:text-nightshade-100 mr-1">{account.name ?? '—'}</span>
        <HeaderField k="Type" v={account.account_type} />
        <HeaderField k="Plan" v={account.plan_interval} />
        <HeaderField k="Product" v={account.product_tier} />
        <HeaderField k="Propensity" v={account.propensity_score} />
        <HeaderField k="Fit" v={account.fit_score} />
        <HeaderField k="Sales Tier" v={account.sales_tier} />
        <span className="ml-auto flex items-center gap-3 text-[12px]">
          <button
            type="button"
            onClick={handleChatClick}
            className="text-content-action dark:text-blueberry-400 hover:underline"
          >
            Chat
          </button>
          <a
            href={`/accounts/${account.id}/activity`}
            className="text-content-action dark:text-blueberry-400 hover:underline"
          >
            Terra ↗
          </a>
          <a
            href={sfdcRecordUrl('Account', account.id)}
            target="_blank" rel="noreferrer"
            className="text-content-action dark:text-blueberry-400 hover:underline"
          >
            SFDC ↗
          </a>
          {account.linkedin && (
            <a href={account.linkedin} target="_blank" rel="noreferrer" className="text-content-action dark:text-blueberry-400 hover:underline">
              LinkedIn ↗
            </a>
          )}
        </span>
      </div>

      {/* Quads. `PANEL_CLASS` gives both cards the SAME explicit `h-[592px]`,
          so the two columns' heights are decoupled from each other's content
          entirely — grid `align-items: stretch` (the default, no
          `items-start` needed) has nothing left to do once both columns
          already have an explicit height. Each body is `flex-1 min-h-0
          overflow-y-auto`: it fills that fixed card height and scrolls
          internally past it, whether its own content is much taller
          (Account Timeline) or much shorter (Intelligence) than Fields. */}
      <div className="grid grid-cols-1 min-[1100px]:grid-cols-2 gap-3">
        <div className={PANEL_CLASS}>
          <div className="px-3 pt-2 pb-1 border-b border-secondary dark:border-nightshade-700">
            <span className="text-[12px] font-semibold text-content-primary dark:text-nightshade-100">Fields</span>
          </div>
          <div className={PANEL_BODY}>
            <AccountFieldsCard
              account={account}
              hierarchy={
                <AccountHierarchyLinks
                  accountId={account.id}
                  accountHref={accountHref}
                  onOpenHierarchyTab={() => setRightTab('hierarchy')}
                  variant="v1"
                />
              }
            />
          </div>
        </div>
        <div className={v1RightCardClass(rightTab === 'hierarchy')} data-testid="account-right-card">
          <div className="flex gap-1 border-b border-secondary dark:border-nightshade-700 px-2 pt-1">
            <QuadTab label="Account Timeline" active={rightTab === 'timeline'} onClick={() => setRightTab('timeline')} />
            <QuadTab label="Opps" active={rightTab === 'opps'} onClick={() => setRightTab('opps')} />
            <QuadTab label="Signals" active={rightTab === 'signals'} onClick={() => setRightTab('signals')} />
            <QuadTab label="Intelligence" active={rightTab === 'intel'} onClick={() => setRightTab('intel')} />
            <QuadTab label="Hierarchy" active={rightTab === 'hierarchy'} onClick={() => setRightTab('hierarchy')} />
          </div>
          <div className={PANEL_BODY}>
            {rightTab === 'timeline' && <AccountTimeline accountId={account.id} />}
            {rightTab === 'opps' && <AccountOppsPanel accountId={account.id} />}
            {rightTab === 'signals' && <AccountSignalsPanel accountId={account.id} />}
            {rightTab === 'intel' && <AccountIntelPanel accountId={account.id} />}
            {rightTab === 'hierarchy' && <AccountHierarchyTab accountId={account.id} accountHref={accountHref} />}
          </div>
        </div>
      </div>

      {/* Workspaces accordion (Bucket E U6) — collapsed by default, between
          the quads and Contacts (prototype's `#workspacesSection`, :409). */}
      <AccountWorkspacesSection
        accountId={account.id}
        onSeeClayUsers={() => setRequestedContactViewId('contact-clay-users')}
      />

      {/* Contacts — the shared component, account-filtered (mount #2) */}
      <div data-testid={CONTACTS_MODULE_TESTID}>
        <div className="text-[13px] font-semibold text-content-primary dark:text-nightshade-100 mb-1.5">Contacts</div>
        <ContactFlowTable
          accountId={account.id}
          requestedViewId={requestedContactViewId}
        />
      </div>
    </div>
  );
}
